import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, RefreshCw, Target, ClipboardCheck, MessageSquare, TrendingUp } from "lucide-react";

import PerformanceDashboard from "./PerformanceDashboard";
import PerformanceCycles from "./PerformanceCycles";
import PerformanceOKRs from "./PerformanceOKRs";
import PerformanceReviews from "./PerformanceReviews";
import PerformanceMyFeedback from "./PerformanceMyFeedback";

export default function PerformanceHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const TABS = [
    { id: "dashboard", icon: LayoutDashboard, section: "pilotage",    label: t("performance.tabs.dashboard.label"), desc: t("performance.tabs.dashboard.desc") },
    { id: "cycles",    icon: RefreshCw,       section: "pilotage",    label: t("performance.tabs.cycles.label"),    desc: t("performance.tabs.cycles.desc") },
    { id: "okrs",      icon: Target,          section: "objectifs",   label: t("performance.tabs.okrs.label"),      desc: t("performance.tabs.okrs.desc") },
    { id: "reviews",   icon: ClipboardCheck,  section: "objectifs",   label: t("performance.tabs.reviews.label"),   desc: t("performance.tabs.reviews.desc") },
    { id: "feedback",  icon: MessageSquare,   section: "selfService", label: t("performance.tabs.feedback.label"),  desc: t("performance.tabs.feedback.desc") },
  ];

  const SECTIONS = ["pilotage", "objectifs", "selfService"];

  const activeTab = searchParams.get("tab") || "dashboard";
  const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true });

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard": return <PerformanceDashboard />;
      case "cycles":    return <PerformanceCycles />;
      case "okrs":      return <PerformanceOKRs />;
      case "reviews":   return <PerformanceReviews />;
      case "feedback":  return <PerformanceMyFeedback />;
      default:          return <PerformanceDashboard />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2
            className="text-2xl font-semibold leading-7 sm:text-3xl"
            style={{ color: "var(--lm-ink)", letterSpacing: "-0.5px" }}
          >
            {t("performance.module.title")}
          </h2>
          <p className="mt-1 text-sm" style={{ color: "var(--lm-ink-2)" }}>
            {t("performance.module.subtitle")}
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
          style={{
            color: "#0ea5e9",
            backgroundColor: "rgba(14,165,233,0.10)",
            border: "1px solid rgba(14,165,233,0.20)",
          }}
        >
          <TrendingUp className="h-3.5 w-3.5" />
          {t("performance.module.badge")}
        </span>
      </div>

      {/* Nav card */}
      <div
        className="rounded-xl"
        style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
      >
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {SECTIONS.map((section) => {
              const sectionTabs = TABS.filter((t) => t.section === section);
              const hasActive = sectionTabs.some((t) => t.id === activeTab);

              return (
                <div
                  key={section}
                  className="rounded-xl"
                  style={{
                    backgroundColor: hasActive ? "var(--lm-s2)" : "var(--lm-s1)",
                    border: hasActive
                      ? "1px solid #0ea5e9"
                      : "1px solid var(--lm-border)",
                    borderLeft: hasActive ? "3px solid #0ea5e9" : undefined,
                  }}
                >
                  <div className="p-5 space-y-3">
                    <h4 className="font-semibold text-sm" style={{ color: "var(--lm-ink)" }}>
                      {t(`performance.sections.${section}`)}
                    </h4>
                    <div style={{ height: "1px", backgroundColor: "var(--lm-border)" }} />
                    <div className="space-y-1">
                      {sectionTabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all text-left text-sm font-medium"
                            style={{
                              backgroundColor: isActive ? "#0ea5e9" : "transparent",
                              color: isActive ? "#ffffff" : "var(--lm-ink-2)",
                              border: isActive ? "1px solid #0ea5e9" : "1px solid transparent",
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.backgroundColor = "rgba(14,165,233,0.08)";
                                e.currentTarget.style.color = "var(--lm-ink)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.color = "var(--lm-ink-2)";
                              }
                            }}
                          >
                            <Icon className="h-4 w-4 flex-shrink-0" />
                            <span className="flex-1">{tab.label}</span>
                            <span className="text-xs" style={{ color: isActive ? "rgba(255,255,255,0.7)" : "var(--lm-ink-3)" }}>
                              {tab.desc}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ height: "1px", backgroundColor: "var(--lm-border)", marginBottom: "24px" }} />

          {renderContent()}
        </div>
      </div>
    </div>
  );
}
